import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { MessageCircle, X, Send, Loader2, HardHat } from "lucide-react";
import { api } from "../lib/api";

const SESSION_KEY = "fd_chat_session";
const HISTORY_KEY = "fd_chat_history";

const SUGGESTIONS = [
  "Di cosa vi occupate?",
  "Avete il servizio sgombero neve?",
  "Come posso richiedere un preventivo?",
  "Quali certificazioni avete?",
];

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [sessionId, setSessionId] = useState(
    () => localStorage.getItem(SESSION_KEY) || null
  );
  const [unread, setUnread] = useState(false);
  const scrollRef = useRef(null);

  // Persist
  useEffect(() => {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (sessionId) localStorage.setItem(SESSION_KEY, sessionId);
  }, [sessionId]);

  // Auto-scroll
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, loading]);

  // Welcome message on first open
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
        {
          role: "assistant",
          text: "Ciao! Sono l'assistente virtuale di Fratelli D'Ambrosio. Come posso aiutarti? Puoi chiedermi informazioni sui servizi, sulle certificazioni o richiedere un preventivo.",
        },
      ]);
    }
  }, [open, messages.length]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    const next = [...messages, { role: "user", text: content }];
    setMessages(next);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", {
        message: content,
        session_id: sessionId,
      });
      if (!sessionId) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      if (!open) setUnread(true);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            "Mi spiace, c'è stato un problema. Riprova tra qualche istante o contattaci direttamente: info@fratellidambrosio.it",
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send();
  };

  const toggle = () => {
    setOpen((o) => !o);
    setUnread(false);
  };

  return (
    <div className="fixed bottom-5 left-5 z-[60] flex flex-col items-start gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="w-[92vw] sm:w-[380px] h-[560px] max-h-[80vh] bg-white rounded-3xl shadow-2xl border border-neutral-200 flex flex-col overflow-hidden"
            data-testid="chatbot-panel"
          >
            {/* Header */}
            <div className="bg-gradient-to-br from-[#1D4ED8] to-[#0B1E3F] text-white p-5 flex items-start justify-between relative overflow-hidden">
              <div className="absolute -bottom-8 -right-6 opacity-15">
                <HardHat className="w-32 h-32" strokeWidth={1.5} />
              </div>
              <div className="relative">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 mb-1">
                  / Assistente virtuale
                </div>
                <div className="font-display font-black text-xl tracking-tighter">
                  Fratelli D'Ambrosio
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                  <span className="text-xs text-white/80">Online ora</span>
                </div>
              </div>
              <button
                onClick={toggle}
                data-testid="chatbot-close"
                className="relative w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Chiudi chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F5F5F3]">
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#1D4ED8] text-white rounded-br-md"
                        : m.error
                        ? "bg-red-50 border border-red-200 text-red-900 rounded-bl-md"
                        : "bg-white border border-neutral-200 text-neutral-800 rounded-bl-md shadow-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </motion.div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-neutral-200 px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-2 shadow-sm">
                    <span className="w-2 h-2 bg-[#1D4ED8] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-[#1D4ED8] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-[#1D4ED8] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}

              {/* Suggestions on first turn only */}
              {messages.length <= 1 && !loading && (
                <div className="pt-2 space-y-2">
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500 px-1 mb-2">
                    Suggerimenti
                  </div>
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      data-testid={`chatbot-suggestion-${i}`}
                      className="w-full text-left text-sm px-4 py-2.5 rounded-xl border border-neutral-200 bg-white hover:border-[#1D4ED8] hover:bg-blue-50 hover:text-[#1D4ED8] transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form onSubmit={onSubmit} className="p-3 border-t border-neutral-200 bg-white flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Scrivi un messaggio..."
                disabled={loading}
                data-testid="chatbot-input"
                className="flex-1 px-4 py-2.5 rounded-full bg-[#F5F5F3] border border-transparent text-sm focus:outline-none focus:border-[#1D4ED8] focus:bg-white transition-colors"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                data-testid="chatbot-send"
                className="w-10 h-10 rounded-full bg-[#1D4ED8] hover:bg-[#0B1E3F] disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors flex-shrink-0"
                aria-label="Invia"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating button */}
      <motion.button
        onClick={toggle}
        data-testid="chatbot-toggle"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[#1D4ED8] to-[#0B1E3F] text-white shadow-xl flex items-center justify-center group"
        aria-label={open ? "Chiudi chat" : "Apri chat"}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X className="w-6 h-6" />
            </motion.span>
          ) : (
            <motion.span
              key="open"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <MessageCircle className="w-6 h-6" />
            </motion.span>
          )}
        </AnimatePresence>

        {/* Pulse ring */}
        {!open && (
          <span className="absolute inset-0 rounded-full bg-[#1D4ED8] opacity-30 animate-ping" />
        )}

        {unread && !open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#60A5FA] border-2 border-white" />
        )}
      </motion.button>
    </div>
  );
}
